import { localStorageKeys } from 'storageKeys';

type SearchOrigin = {
  ver: string;
  system_model: string;
  search: string;
  spold_id: string;
};

const isSearchOrigin = (value: unknown): value is SearchOrigin => {
  const candidate = value as Partial<SearchOrigin> | null;
  return (
    typeof candidate?.search === 'string' &&
    typeof candidate?.ver === 'string' &&
    typeof candidate?.system_model === 'string' &&
    typeof candidate?.spold_id === 'string'
  );
};

export const saveSearchOrigin = ({
  ver,
  system_model,
  search,
  spold_id,
}: Omit<SearchOrigin, 'spold_id'> & { spold_id: string | number }) => {
  try {
    localStorage.setItem(
      localStorageKeys.SEARCH_ORIGIN,
      JSON.stringify({ ver, system_model, search, spold_id: String(spold_id) })
    );
  } catch {
    // Storage throws when full or blocked; losing the search origin is acceptable.
  }
};

const loadSearchOrigin = (): SearchOrigin | null => {
  try {
    const stored = localStorage.getItem(localStorageKeys.SEARCH_ORIGIN);
    if (!stored) return null;

    const parsed = JSON.parse(stored);
    return isSearchOrigin(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

type SearchPathParams = {
  ver: string;
  system_model: string;
  spold_id?: string;
  datasetName?: string;
};

/** Filter values aren't portable between releases, so a search from another release is dropped. */
export const getSearchPath = ({ ver, system_model, spold_id, datasetName }: SearchPathParams) => {
  const basePath = `/${ver}/${system_model}/search`;
  const origin = loadSearchOrigin();

  const cameFromSearch =
    !!origin && !!spold_id && origin.spold_id === spold_id && origin.ver === ver && origin.system_model === system_model;

  if (cameFromSearch) {
    const recordedSearch = origin.search.replace(/^\?/, '');
    if (recordedSearch) return `${basePath}?${recordedSearch}`;
  }

  if (!datasetName) return basePath;

  return `${basePath}?${new URLSearchParams({ query: datasetName })}`;
};
